// #region imports
import { Draft, produce } from 'immer';
import {
  SheetView,
  GridEntity,
  IRange,
  Sheet,
  SelectionCell,
  SheetSelection,
  EntitySize,
} from '../../types';
import {
  HideUnhideGrid,
  ResizeGrid,
  SheetCommandType,
} from '../../types/commands/sheetCommands/sheetCommands';
import {
  convertPositionToArea,
  createSheetSelection,
  getDefaultPosition,
  getIntersectedArea,
  getMaxGridArea,
  reduceAreaByIntersection,
} from '../area/area';
import { setActiveCell } from '../selection/selection';
import { getEntitiesData } from '../sheet/uiData';
import {
  areConsecutiveRanges,
  doesRangeInterects,
  getIntersectedRange,
  isEqualRanges,
  isIndexWithinRange,
  isRangeInsideRange,
  reduceRangeByIntersection,
  splitRange,
  unionRanges,
} from '../area/range';
import { getUnHiddenPosition } from '../views/virtualViewport';
import { DataDirection } from '../../../core/types/ui/position';
import { unionAreaWithMerges } from '../views/merges';
import { SheetManagementSet } from '../../types/sheet/draft';
import { saveSheetUpdatesInSet } from '../sheet/updates';
import _, { merge, range } from 'lodash';
import groupColors from '../../constants/groupColors';
import { HeaderGroup } from '../../types/sheet/headerGroups';
import { v4 as uuid } from 'uuid';
import { select } from 'redux-saga/effects';
import { inRange } from '../../../core/utils/general/general';
// #endregion

export const getRangesFromSelection = (
  sheetView: SheetView,
  entity: GridEntity
): IRange[] => {
  const alias = entity === GridEntity.Column ? 'ci' : 'ri';
  return sheetView.selections.map(({ area }) => ({
    start: area.start[alias],
    end: area.end[alias],
  }));
};

// #region hide un hide selection utils
export const adjustSelectionOnHideUnhide = (
  set: SheetManagementSet,
  sheet: Sheet,
  sheetView: SheetView,
  // sheetUpdates: Pick<Sheet, 'hiddenCols' | 'hiddenRows'>,
  ranges: IRange[],
  cmd: HideUnhideGrid,
  revealedRanges?: IRange[]
) => {
  const isColumn = cmd.entity === GridEntity.Column;
  const alias = isColumn ? 'ci' : 'ri';

  const miniView: Pick<
    SheetView,
    'selections' | 'activeCell' | 'activeCellArea'
  > = {
    selections: sheetView.selections,
    activeCell: sheetView.activeCell,
    activeCellArea: sheetView.activeCellArea,
  };

  const updatedSheetView = produce(miniView, (draft) => {
    if (cmd.type === SheetCommandType.HideGrid) {
      adjustSelectionOnHide(
        sheet,
        sheetView,
        draft,
        // sheetUpdates,
        alias,
        isColumn
      );
    } else {
      const targetRanges = revealedRanges ?? ranges;
      adjustSelectionOnUnhide(
        sheet,
        sheetView,
        draft,
        targetRanges,
        alias,
        isColumn
      );
    }
  });

  saveSheetUpdatesInSet(sheet.id, set, { updatedSheetView });
};

const adjustSelectionOnUnhide = (
  sheet: Draft<Sheet>,
  sheetView: SheetView,
  draft: Draft<Pick<SheetView, 'activeCell' | 'selections' | 'activeCellArea'>>,
  ranges: IRange[],
  alias: 'ci' | 'ri',
  isColumn: boolean
) => {
  if (!ranges || !ranges.length) return;

  const maxArea = getMaxGridArea(sheet.maxColIndex, sheet.maxRowIndex);
  const cellType = isColumn ? SelectionCell.Column : SelectionCell.Row;

  const range = ranges[0];
  maxArea.start[alias] = range.start;
  maxArea.end[alias] = range.end;
  const newSelection = createSheetSelection(maxArea, cellType);
  draft.selections = [newSelection];

  const results = createCellPositionForHidden(
    sheet,
    sheetView,
    newSelection,
    alias,
    cellType
  );
  draft.activeCell = results.activeCell;
  draft.activeCellArea = results.activeCellArea;
};

const adjustSelectionOnHide = (
  sheet: Draft<Sheet>,
  sheetView: SheetView,
  draft: Draft<Pick<SheetView, 'activeCell' | 'selections' | 'activeCellArea'>>,
  // newSheetUpdates: Pick<Sheet, 'hiddenCols' | 'hiddenRows'>,
  alias: 'ci' | 'ri',
  isColumn: boolean
) => {
  draft.selections.map((selection, index) => {
    const area = selection.area;
    const currentPos = area.start[alias];
    const entityData = getEntitiesData(sheet, sheetView, isColumn);
    const hiddenRanges = isColumn
      ? sheet.hiddenCols
      : sheet.hiddenRows;

    if (isIndexWithinRange(currentPos, hiddenRanges).inRange) {
      const newPos = getUnHiddenPosition(
        entityData.entitySizes,
        hiddenRanges,
        currentPos,
        entityData.maxIndex,
        DataDirection.Forward,
        entityData.freeze,
        entityData.filterResults,
        0
      );
      area.start[alias] = newPos;
      area.end[alias] = newPos;
      selection.area = unionAreaWithMerges(sheet.merges, area);
      draft.activeCell[alias] = newPos;
      draft.activeCellArea = unionAreaWithMerges(
        sheet.merges,
        convertPositionToArea(draft.activeCell)
      );

      return selection;
    }
  });
};

const createCellPositionForHidden = (
  sheet: Sheet,
  sheetView: SheetView,
  selection: SheetSelection,
  selectionAlias: 'ci' | 'ri',
  cellType: SelectionCell
) => {
  const cellPos = getDefaultPosition();
  cellPos[selectionAlias] = selection.area.start[selectionAlias];
  return setActiveCell(
    cellPos,
    sheetView.colViewport,
    sheetView.rowViewport,
    cellType,
    sheet
  );
};
// #endregion

// #region group utils

export const addRangesToGroups = (groups: HeaderGroup[], ranges: IRange[]) => {
  for (const range of ranges) {
    groups = addRangeToGroup(groups, range);
  }

  return groups;
};

const addRangeToGroup = (groups: HeaderGroup[], range: IRange) => {
  let updatedGroups: HeaderGroup[] = [];

  let rangeAdded = false;
  let checkGroupCollapsing = false;

  for (const group of groups) {
    const groupRange = {
      start: group.start,
      end: group.end,
    };

    if (isRangeInsideRange(groupRange, range)) {
      group.nestedGroups = addRangeToGroup(group.nestedGroups, range);
      updatedGroups.push(group);
      rangeAdded = true;
    } else if (doesRangeInterects(groupRange, range)) {
      const mergedGroup: HeaderGroup = {
        ...group,
        start: Math.min(group.start, range.start),
        end: Math.max(group.end, range.end),
      };
      updatedGroups.push(mergedGroup);
      rangeAdded = true;
      checkGroupCollapsing = true;
    } else {
      updatedGroups.push(group);
    }
  }

  if (!rangeAdded) {
    updatedGroups.push({
      id: uuid(),
      start: range.start,
      end: range.end,
      nestedGroups: [],
      name: '',
      color: _.sample(groupColors),
    });
  } else if (checkGroupCollapsing) {
    // Check for group collapsing
    updatedGroups = mergeCollapsingGroups(updatedGroups);
  }

  return updatedGroups;
};

const mergeCollapsingGroups = (groups: HeaderGroup[]) => {
  let mergedGroups: HeaderGroup[] = [];
  for (const group of groups) {
    let isMerged = false;
    const group1Range = {
      start: group.start,
      end: group.end,
    };
    for (const mergedGroup of mergedGroups) {
      const mergedGroupRange = {
        start: mergedGroup.start,
        end: mergedGroup.end,
      };

      if (doesRangeInterects(group1Range, mergedGroupRange)) {
        // Groups intersect, merge them
        mergedGroup.start = Math.min(group.start, mergedGroup.start);
        mergedGroup.end = Math.max(group.end, mergedGroup.end);
        mergedGroup.nestedGroups.push(...group.nestedGroups);
        mergedGroup.name = mergedGroup.name.length
          ? mergedGroup.name
          : group.name;
        mergedGroup.color = mergedGroup.color ? mergedGroup.color : group.color;

        isMerged = true;
        break;
      }
    }

    if (!isMerged) {
      // If the group was not merged, add it to the mergedGroups array
      mergedGroups.push(group);
    }
  }

  return mergedGroups;
};

export const removeGroupById = (
  groups: HeaderGroup[],
  groupId: string
): HeaderGroup[] => {
  return groups.filter((group) => {
    if (group.id === groupId) {
      return false;
    }

    if (group.nestedGroups && group.nestedGroups.length > 0) {
      group.nestedGroups = removeGroupById(group.nestedGroups, groupId);

      return true;
      // If any nested group remains after removal
      // return group.nestedGroups.length > 0;
    }
    return true;
  });
};
// #endregion

// #region resize ranges utils
export const generateResizeRanges = (
  sheetView: SheetView,
  command: ResizeGrid
): EntitySize[] => {
  const { entity, positionIndex, rangeSizes, size } = command;

  if (rangeSizes) return rangeSizes;

  if (positionIndex !== undefined && size !== undefined) {
    return createResizeRangeFromPosition(
      sheetView.selections,
      entity,
      positionIndex,
      size
    );
  }

  return [];
};

const createResizeRangeFromPosition = (
  selections: SheetSelection[],
  entity: GridEntity,
  positionIndex: number,
  size: number
): EntitySize[] => {
  const isColumn = entity === GridEntity.Column;
  const alias = isColumn ? 'ci' : 'ri';
  const cell = isColumn ? SelectionCell.Column : SelectionCell.Row;

  let positionLiesInsideSelection = false;
  const targetRanges: EntitySize[] = [];

  selections.forEach((selection) => {
    const { area } = selection;
    const selectionStart = area.start[alias];
    const selectionEnd = area.end[alias];

    const positionWithinSelection =
      inRange(positionIndex, selectionStart, selectionEnd) &&
      selection.cell === cell;

    if (positionWithinSelection) {
      positionLiesInsideSelection = true;
    }

    if (selection.cell === cell) {
      targetRanges.push({
        start: selectionStart,
        end: selectionEnd,
        size,
      });
    }
  });

  console.log(
    'positionLiesInsideSelection',
    positionLiesInsideSelection,
    targetRanges
  );

  if (positionLiesInsideSelection) return targetRanges;

  const defaultRange: EntitySize = {
    start: positionIndex,
    end: positionIndex,
    size,
  };

  return [defaultRange];
};

// export const removeRangeFromEntitySizes = (
//   entitySizes: EntitySize[],
//   range: IRange
// ) => {
//   const updatedEntitySizes: EntitySize[] = [];

//   for (const entitySize of entitySizes) {
//     if (doesRangeInterects(entitySize, range)) {
//       const newRanges = splitRange(entitySize, range);
//       if (newRanges.length) {
//         const rangesWithSameSize = newRanges.map((range) => ({
//           ...range,
//           size: entitySize.size,
//         }));
//         updatedEntitySizes.push(...rangesWithSameSize);
//       }
//     } else {
//       updatedEntitySizes.push(entitySize);
//     }
//   }

//   return updatedEntitySizes;
// };

export const addRangeToEntitySizes = (
  sizes: EntitySize[],
  newRangeSize: EntitySize,
  defaultSize: number
) => {
  const updatedSizes: EntitySize[] = [];
  const intersectedSizes: EntitySize[] = [];

  sizes.forEach((rangeSize) => {
    const intersection = getIntersectedRange(rangeSize, newRangeSize);
    if (intersection) {
      reduceRangeByIntersection(rangeSize, intersection).forEach((rangeChunk) =>
        updatedSizes.push({ ...rangeChunk, size: rangeSize.size })
      );

      intersectedSizes.push({
        ...intersection,
        size: rangeSize.size,
      });
    } else {
      updatedSizes.push(rangeSize);
    }
  });

  updatedSizes.push(newRangeSize);

  // Instead of this why not apply default size to current area First it will help
  // let remainingRanges: EntitySize[] = [
  //   {
  //     ...newRangeSize,
  //     size: defaultSize,
  //   },
  // ];
  // intersectedSizes.forEach((intersectedSize) => {
  //   let rR: EntitySize[] = [];
  //   remainingRanges.forEach((rangeSize) => {
  //     const intersection = getIntersectedRange(intersectedSize, rangeSize);

  //     if (intersection) {
  //       reduceRangeByIntersection(rangeSize, intersection).forEach((newRange) =>
  //         rR.push({ ...newRange, size: defaultSize })
  //       );
  //     } else {
  //       rR.push(rangeSize);
  //     }
  //   });
  //   remainingRanges = rR;
  // });

  // const oldSizes = intersectedSizes.concat(remainingRanges);
  const resetDefaultSize = { ...newRangeSize, size: defaultSize };
  const oldSizes = [resetDefaultSize, ...intersectedSizes];

  return {
    updatedSizes: mergeConsecutiveEntitySizes(updatedSizes),
    oldSizes,
  };
};

// export const addRangeToEntitySizes = (
//   entitySizes: EntitySize[],
//   newRange: IRange,
//   newSize: number,
//   defaultSize: number
// ) => {
//   let rangeEvaluated = false;
//   let checkCollapsing = false;
//   let oldEntitySizes: EntitySize[] = [];
//   const updatedSizes: EntitySize[] = [];
//   for (const entitySize of entitySizes) {
//     const { size: oldSize, ...oldRange } = entitySize;
//     if (isRangeInsideRange(oldRange, newRange)) {
//       // Split ranges according to sizes
//       // If newSize and size are same then skip
//       rangeEvaluated = true;

//       if (oldSize === newSize) {
//         updatedSizes.push(entitySize);
//       } else {
//         splitRange(oldRange, newRange).forEach((range) => {
//           updatedSizes.push({ ...range, size: oldSize });
//         });
//         updatedSizes.push({ ...newRange, size: newSize });
//       }
//       oldEntitySizes.push({ ...newRange, size: oldSize });
//     } else if (isRangeInsideRange(newRange, oldRange)) {
//       updatedSizes.push({ ...newRange, size: newSize });
//       rangeEvaluated = true;
//       checkCollapsing = true;

//       oldEntitySizes.push({ ...oldRange, size: oldSize });
//       splitRange(newRange, oldRange).forEach((range) => {
//         oldEntitySizes.push({ ...range, size: defaultSize });
//       });
//     } else if (doesRangeInterects(oldRange, newRange)) {
//       //  Split range from intersection
//       // If newSize and size are same then unionRanges
//       checkCollapsing = true;
//       rangeEvaluated = true;
//       if (oldSize === newSize) {
//         updatedSizes.push({
//           ...unionRanges(oldRange, newRange),
//           size: newSize,
//         });
//       } else {
//         splitRange(oldRange, newRange).forEach((range) =>
//           updatedSizes.push({ ...range, size: oldSize })
//         );
//         updatedSizes.push({ ...newRange, size: newSize });
//       }
//     } else if (areConsecutiveRanges(oldRange, newRange)) {
//       // If sizes are same them union ranges
//       // If sizes are different save as different set
//       rangeEvaluated = true;
//       if (oldSize === newSize) {
//         updatedSizes.push({
//           ...unionRanges(oldRange, newRange),
//           size: newSize,
//         });
//       } else {
//         updatedSizes.push(entitySize);
//         updatedSizes.push({ ...newRange, size: newSize });
//       }
//     } else {
//       updatedSizes.push(entitySize);
//     }
//   }

//   console.log('checkCollapsing', checkCollapsing, updatedSizes);

//   if (!rangeEvaluated) {
//     updatedSizes.push({
//       ...newRange,
//       size: newSize,
//     });
//   } else if (checkCollapsing) {
//     return mergeCollapsingEntitySizes(updatedSizes);
//   }

//   return updatedSizes;
// };

// const mergeCollapsingEntitySizes = (entitySizes: EntitySize[]) => {
//   let mergedSizes: EntitySize[] = [];
//   for (const entitySize of entitySizes) {
//     let isMerged = false;
//     for (const mergedSize of mergedSizes) {
//       if (
//         doesRangeInterects(entitySize, mergedSize) &&
//         entitySize.size === mergedSize.size
//       ) {
//         const newRange = unionRanges(entitySize, mergedSize);
//         mergedSize.start = newRange.start;
//         mergedSize.end = newRange.end;

//         isMerged = true;
//         break;
//       }
//     }
//     if (!isMerged) {
//       // If the range was not merged, add it to the mergedSizes array
//       mergedSizes.push(entitySize);
//     }
//   }

//   return mergedSizes;
// };

export const mergeConsecutiveEntitySizes = (
  entitySizes: EntitySize[]
): EntitySize[] => {
  let mergedSizes: EntitySize[] = [];
  for (const entitySize of entitySizes) {
    let isMerged = false;

    for (const mergedSize of mergedSizes) {
      if (
        (areConsecutiveRanges(entitySize, mergedSize) ||
          doesRangeInterects(entitySize, mergedSize)) &&
        entitySize.size === mergedSize.size
      ) {
        const newRange = unionRanges(entitySize, mergedSize);
        mergedSize.start = newRange.start;
        mergedSize.end = newRange.end;

        isMerged = true;
        break;
      }
    }

    if (!isMerged) {
      // If the range was not merged, add it to the mergedSizes array
      mergedSizes.push({ ...entitySize });
    }
  }

  const result =
    mergedSizes.length === entitySizes.length
      ? mergedSizes
      : mergeConsecutiveEntitySizes(mergedSizes);

  return result;
};
// #endregion
